import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Star, ArrowRight, Phone } from "lucide-react";
import { COMPANY, STATS } from "../data/site";
import { useEstimate } from "../context/EstimateContext";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { openEstimate } = useEstimate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pt-[84px]">
      <div className="absolute inset-0 hero-grid-bg opacity-70" />
      <div className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-[var(--apex-accent)]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 lg:pt-16 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600"
          >
            <MapPin size={14} className="text-[var(--apex-accent)]" />
            Serving {COMPANY.area}
          </motion.div>

          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.0] text-[var(--apex-primary)] mt-6">
            <Line delay={0.15}>Home remodeling,</Line>
            <Line delay={0.28}><span className="text-[var(--apex-accent)]">built the right way.</span></Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed"
          >
            Apex Home Pro Inc is your trusted Lehigh Valley general contractor for kitchens,
            bathrooms, decks, framing, flooring and full renovations — quality craftsmanship,
            honest pricing, one dependable crew.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-estimate-btn"
              onClick={openEstimate}
              className="group relative inline-flex items-center justify-center gap-2 rounded-sm bg-[var(--apex-accent)] text-white font-semibold px-7 py-4 overflow-hidden transition-colors"
            >
              <span className="absolute inset-0 bg-[var(--apex-accent-dark)] translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              <span className="relative">Get a Free Estimate</span>
              <ArrowRight size={18} className="relative group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="hero-phone-btn"
              className="inline-flex items-center justify-center gap-2 rounded-sm border border-slate-300 bg-white text-[var(--apex-primary)] font-semibold px-7 py-4 hover:border-slate-400 transition-colors"
            >
              <Phone size={18} /> {COMPANY.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-10 flex items-center gap-2 text-sm text-slate-500"
          >
            <div className="flex text-[var(--apex-accent)]">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
            </div>
            Rated 4.9 by homeowners across the Valley
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.2 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative rounded-md overflow-hidden shadow-2xl ring-1 ring-slate-200 h-[400px] lg:h-[540px]">
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src="https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Modern kitchen remodel by Apex Home Pro in the Lehigh Valley"
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
          </div>
          <motion.div
            style={{ y: badgeY }}
            className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-md shadow-xl ring-1 ring-slate-200 px-5 py-4 flex gap-6"
          >
            {STATS.slice(0, 2).map((s) => (
              <div key={s.label}>
                <div className="font-display font-black text-2xl text-[var(--apex-primary)]">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <div className="relative border-y border-slate-200 bg-white/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-200">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="py-6 px-4 text-center"
            >
              <div className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-accent)]">{s.value}</div>
              <div className="text-xs sm:text-sm text-slate-500 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
